import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { View } from 'react-native';
import parseByDelimiter from '@utils/parseByDelimiter';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import ReactDataGrid from "@inovua/reactdatagrid-community";
import navigate from '../../../rootNavigation';
import { CELL_BORDER_RADIUS } from '@navigation/screens/TopLevel/Components/Styles';
import ErrorBoundary from '@utils/ErrorBoundary';

export const gridStyle = {
  flex: 1,
  paddingRight: 20,
  borderRadius: 20,
  alignSelf: 'stretch',
  flexDirection: 'row',
  fontFamily: 'Montserrat',
};

const IndividualDataTable = ({ plain = false, dataArray, clickFunction, dataSchema }) => {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const theme = themeSelector();
  const [dataLoading, setDataLoading] = useState(true);

  const extractData = (obj) => {
    if (typeof obj === 'object' && obj !== null) {
      if ('data' in obj || 'text' in obj) {
        if (typeof obj.data !== 'object' || typeof obj.text !== 'object' || Array.isArray(obj.data)) {
          if (Array.isArray(obj.data)) {
            return obj.data.map((item) => {
              if (item.data && !Array.isArray(item.data)) {
                return Object.fromEntries(
                  Object.entries(item.data).map(([key, value]) => {
                    return (
                      typeof value === 'object' && !Array.isArray(value)
                        ? [key, JSON.stringify(value)]
                        : [key, value])
                  }
                  )
                );
              }
              return extractData(item);
            });
          }
          return obj.data || obj.text;
        } else {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [key, extractData(value)])
          );
        }
      }
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, extractData(value)])
      );
    } else if (Array.isArray(obj)) {
      //Check if array only contains strings
      if (obj.every((item) => typeof item === 'string')) {
        return JSON.stringify(obj);
      }
      return obj.map((item) => extractData(item));
    }
    else if (typeof obj === 'string') {
      return obj;
    }
    return null;
  };

  const generateColumns = useCallback(() => {
    return dataSchema.map((key) => ({
      name: key.title,
      defaultFlex: 1,
      header: parseByDelimiter(key.title),
      innerWidth: key?.width || 140,
      // You can also define specific filterEditor or filterEditorProps for each column here
      // filterEditor: (props) => <MyCustomFilterEditor {...props} />,
      // filterEditorProps: { placeholder: `Filter ${key.title}...` },
    }));
  }, [dataSchema]);

  useEffect(() => {
    try {
      setDataLoading(true);
      if (dataArray) {
        const newData = plain
          ? dataArray.map((item) => {
            const obj = {};
            item.forEach((element, index) => {
              obj[dataSchema[index].title] = element;
            });
            return obj;
          })
          : dataArray.map((item) => {
            if (item.data && !Array.isArray(item.data)) {
              return Object.fromEntries(
                Object.entries(item.data).map(([key, value]) => {
                  return (
                    typeof value === 'object' && !Array.isArray(value)
                      ? [key, JSON.stringify(value)]
                      : [key, value])
                }
                )
              );
            }
            return extractData(item);
          });

        setData(newData);
        setDataLoading(false);
      }
    } catch (error) {
      setDataLoading(false);
      console.error('Error processing dataArray:', error);
    }
  }, [dataArray, plain, dataSchema]);

  const columns = useMemo(generateColumns, [dataSchema]);

  return (
    <View style={{ flex: 1, borderRadius: 20, alignSelf: 'stretch', backgroundColor: theme ? "white" : "grey" }}>
      <ErrorBoundary>
        <ReactDataGrid
          idProperty="table"
          columns={columns}
          className='my-custom-grid'
          loading={dataLoading}
          emptyText="No data"
          onSelectionChange={({ selected }) => setSelected((old) => [...old, selected])}
          onCellClick={(_, cellProps) => {
            if (clickFunction) {
              clickFunction(cellProps.data);
            } else {
              navigate('Data', {
                screen: 'TimeLineData',
                params: {
                  data: cellProps.data,
                },
              });
            }
          }}
          multiSelect
          selected={selected}
          columnMinWidth={200}
          columnDefaultWidth={220}
          dataSource={data}
          style={gridStyle}
          // --- ADDED PROP FOR FILTERING ---
          enableFiltering={true}
        // --------------------------------
        />
      </ErrorBoundary>
    </View>
  );
};

export default IndividualDataTable;
